import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {Set<number>} */
  #loadedPages = new Set();

  /** @type {Set<number>} */
  #loadingPages = new Set();

  /** @type {boolean} */
  #isPreloadingAllPages = false;

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#syncLoadedPagesFromDom();

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    if (this.sortInStockFirst) {
      requestIdleCallback(() => {
        this.#sortByAvailability();
        this.#preloadAllPagesForStockSort();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;

    if (this.#loadedPages.has(nextPage.page)) {
      requestIdleCallback(() => this.#fetchPage('next'));
      return;
    }

    let appended = await this.#appendPageIfNeeded(nextPage.page, nextPage.url);

    if (!appended) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      this.#fetchPage('next');

      await promise;
      appended = await this.#appendPageIfNeeded(nextPage.page, nextPage.url);
      if (!appended) return;
    }

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    if (this.#loadedPages.has(previousPage.page)) {
      requestIdleCallback(() => this.#fetchPage('previous'));
      return;
    }

    const { grid } = this.refs;
    if (!grid) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    grid.prepend(...previousPageItemElements);
    this.#loadedPages.add(previousPage.page);

    this.#aspectRatioHelper?.processNewElements();
    this.#sortByAvailability();

    history.pushState('', '', previousPage.url.toString());

    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {number} pageNumber
   * @param {URL} [url]
   * @returns {Promise<boolean>}
   */
  async #appendPageIfNeeded(pageNumber, url = undefined) {
    if (this.#loadedPages.has(pageNumber) || this.#loadingPages.has(pageNumber)) {
      return false;
    }

    const { grid } = this.refs;
    if (!grid) return false;

    this.#loadingPages.add(pageNumber);

    let items = this.#getGridForPage(pageNumber);

    if (!items?.length) {
      await this.#fetchSpecificPage(pageNumber, url);
      items = this.#getGridForPage(pageNumber);
    }

    if (!items?.length) {
      this.#loadingPages.delete(pageNumber);
      return false;
    }

    grid.append(...items);
    this.#loadedPages.add(pageNumber);
    this.#loadingPages.delete(pageNumber);

    this.#aspectRatioHelper?.processNewElements();
    this.#sortByAvailability();

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';

    if (this.sortInStockFirst && this.#loadedPages.size > 0) {
      const page = isPrevious ? Math.min(...this.#loadedPages) - 1 : Math.max(...this.#loadedPages) + 1;
      const url = new URL(window.location.href);
      url.searchParams.set('page', page.toString());
      url.hash = '';

      return { page, url };
    }

    const { cards } = this.refs;

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > .product-grid__item[data-product-id]');
  }

  /**
   * @param {HTMLElement} grid
   * @returns {HTMLElement[]}
   */
  #getGridItems(grid) {
    return [...grid.querySelectorAll(':scope > .product-grid__item[data-product-id]')].filter(
      (item) => item instanceof HTMLElement
    );
  }

  #syncLoadedPagesFromDom() {
    const { grid } = this.refs;
    if (!grid) return;

    for (const item of this.#getGridItems(grid)) {
      if (item.dataset.page) {
        this.#loadedPages.add(Number(item.dataset.page));
      }
    }

    if (this.#loadedPages.size === 0) {
      this.#loadedPages.add(1);
    }
  }

  async #preloadAllPagesForStockSort() {
    if (!this.sortInStockFirst || this.#isPreloadingAllPages) return;

    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage || 1);

    if (lastPage <= 1) {
      this.#sortByAvailability();
      return;
    }

    this.#isPreloadingAllPages = true;
    this.#syncLoadedPagesFromDom();

    let nextPage = Math.max(...this.#loadedPages) + 1;

    while (nextPage <= lastPage) {
      if (!this.isConnected) break;
      if (!this.#shouldUsePage({ page: nextPage })) break;

      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage.toString());
      url.hash = '';

      await this.#appendPageIfNeeded(nextPage, url);
      nextPage = Math.max(...this.#loadedPages) + 1;

      await new Promise((resolve) => {
        requestIdleCallback(() => resolve(undefined));
      });
    }

    this.#isPreloadingAllPages = false;
    this.#sortByAvailability();
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  get sortInStockFirst() {
    return this.hasAttribute('sort-in-stock-first');
  }

  #sortByAvailability() {
    if (!this.sortInStockFirst) return;

    const { grid } = this.refs;
    if (!grid) return;

    const items = this.#getGridItems(grid);
    if (items.length < 2) return;

    const sorted = items
      .map((item, index) => ({
        item,
        index,
        available: item.dataset.available !== 'false',
      }))
      .sort((a, b) => {
        if (a.available !== b.available) {
          return a.available ? -1 : 1;
        }

        return a.index - b.index;
      })
      .map(({ item }) => item);

    const needsReorder = items.some((item, index) => item !== sorted[index]);
    if (!needsReorder) return;

    const fragment = document.createDocumentFragment();
    for (const item of sorted) {
      fragment.appendChild(item);
    }

    grid.appendChild(fragment);
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#loadedPages = new Set();
    this.#loadingPages = new Set();
    this.#isPreloadingAllPages = false;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        this.#syncLoadedPagesFromDom();

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
        this.#sortByAvailability();
        this.#preloadAllPagesForStockSort();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
